<template>
  <span>
    <b-button
              variant="edit"
              @click="showTaskModal(task)"
              qid="delete-s-cancel-button"
    >
    <span v-if="task">
      <font-awesome-icon icon="pen"/>
    </span>
    <span v-if="!task">
      {{ $t('tasks.open_first_task') }}
    </span>
    </b-button>

    <!-- Task panel, in the engagement task panel's design (US 9478) -->
    <TaskPanelFrame ref="task-modal"
                    qid="control-task-panel"
                    :crumb="selectedTask && selectedTask.project ? [selectedTask.project.title, selectedTask.model ? selectedTask.model.name : ''] : []"
                    :title="selectedTask && selectedTask.scope ? selectedTask.scope.name : (selectedTask ? selectedTask.title : '')"
                    :status="selectedTask ? selectedTask.status : ''"
                    :users="selectedTask && selectedTask.users ? selectedTask.users : []"
                    :due-date="selectedTask && selectedTask.project ? selectedTask.project.end_date : ''"
                    @close="hideTaskModal"
    >
      <template #meta>
        <span v-if="selectedTask && selectedTask.model">
          <a href="#" class="v2-link" @click.prevent="navigateToTask(selectedTask)" data-qid="control-task-panel-asset">
            <font-awesome-icon :icon="assetIcon(selectedTask.model)" class="mr-1"/>{{ selectedTask.model.name }}
          </a>
        </span>
      </template>

      <!-- What the control asks for, with its files and links -->
      <div class="task-control" v-if="selectedTask && selectedTask.scope && selectedTask.scope.name" data-qid="task-panel-control">
        <div class="task-control__label"><font-awesome-icon icon="shield-alt" class="mr-1"/>{{ selectedTask.scope.name }}</div>
        <div class="task-control__text text-formatted" v-if="selectedTask.scope.description" v-html="selectedTask.scope.description"></div>
        <div v-for="file in selectedTask.scope.media || []" :key="'control-file-' + file.id" class="task-panel-help__item">
          <font-awesome-icon icon="paperclip" class="mr-1 text-muted"/>
          <a href="#" @click.prevent="downloadScopeMedia(file)">{{ file.file_name }}</a>
        </div>
        <div v-for="link in selectedTask.scope.links || []" :key="'control-link-' + link.id" class="task-panel-help__item">
          <font-awesome-icon icon="link" class="mr-1 text-muted"/>
          <a :href="link.url" target="_blank" rel="noopener">{{ link.name ? link.name : link.url }}</a>
        </div>
      </div>
      <b-alert v-if="error.length" show variant="danger" qid="create-p-error">{{ error }}</b-alert>
      <b-alert v-if="selectedTask && selectedTask.model && !selectedTask.scope" show variant="danger" qid="create-p-error">
        {{ $t('tasks.generic_control_not_available') }}
      </b-alert>

      <!-- The result of the control, as buttons (US 9478) -->
      <section class="v2-field" v-if="selectedTask && selectedTask.model && selectedTask.scope" data-qid="control-task-result">
        <header class="v2-field__head">
          <h4>{{ $t('tasks.control_result.title') }}<span class="text-red ml-1">*</span></h4>
        </header>
        <div class="task-choices">
          <span v-for="option in allAnswers"
                :key="'control-result-' + option.value"
                class="task-choice"
                :class="['task-choice--' + option.tone, {'task-choice--on': selectedAnswer && selectedAnswer.value === option.value}]"
                @click="selectedAnswer = option"
                :data-qid="'control-result-' + option.value"
          >
            <font-awesome-icon :icon="option.icon"/>{{ option.name }}
          </span>
        </div>
      </section>

      <!-- Comment, in rich text -->
      <section class="v2-field"
               v-if="selectedTask && selectedTask.scope && (selectedTask.add_description == null || selectedTask.add_description != 'No')"
               data-qid="control-task-comment"
      >
        <header class="v2-field__head">
          <h4>{{ $t('tasks.control_result.comment') }}<span class="text-red ml-1" v-if="selectedTask.add_description == 'Mandatory'">*</span></h4>
        </header>
        <FormattedNoteEditor v-model="taskForm.remarks"
                             :rows="5"
                             :placeholder="$t('tasks.control_result.comment_placeholder')"
                             qid="create-s-form-description"
        ></FormattedNoteEditor>
      </section>

      <!-- Attachments and links, as on control assessment tasks -->
      <section class="v2-field v2-field--flush"
               v-if="selectedTask && selectedTask.scope && (selectedTask.add_attachments == null || selectedTask.add_attachments != 'No')"
               data-qid="task-attachments"
      >
        <header class="v2-field__head">
          <h4>{{ $t('attachments.label') }}<span class="text-red ml-1" v-if="selectedTask.add_attachments == 'Mandatory'">*</span></h4>
          <span class="v2-field__meta">{{ (selectedTask.media || []).length + (selectedTask.links || []).length }}</span>
          <label class="btn btn-secondary btn-sm mb-0 cursor-pointer" qid="create-p-file-button">
            <input type="file"
                   name="fileupload"
                   value="fileupload"
                   multiple
                   id="file"
                   class="d-none"
                   @change="onFileChange"
                   @click="resetFile"
            >
            <font-awesome-icon icon="cloud-upload-alt" class="mr-1"/>{{ $t('system.upload') }}
          </label>
          <b-button size="sm" variant="secondary" @click="showInput = true" qid="add-another-link">
            <font-awesome-icon icon="link" class="mr-1"/>{{ $t('system.add_link') }}
          </b-button>
        </header>
        <div v-for="file in selectedTask.media || []" :key="'file-' + file.id" class="task-file">
          <font-awesome-icon icon="paperclip" class="task-file__icon"/>
          <div class="task-file__text">
            <a href="#" @click.prevent="downloadMedia(file)" qid="create-p-media-item">{{ file.file_name }}</a>
          </div>
          <span class="v2-field__icon" @click="downloadMedia(file)" v-b-tooltip.hover :title="$t('tasks.v2.download')" qid="download-media">
            <font-awesome-icon icon="cloud-download-alt"/>
          </span>
          <span class="v2-field__icon v2-field__icon--danger" @click="openDeleteAttachmentModal(file)" v-b-tooltip.hover :title="$t('attachments.delete.title')" qid="remove-media-item">
            <font-awesome-icon icon="trash-alt"/>
          </span>
        </div>
        <div v-for="link in selectedTask.links || []" :key="'link-' + link.id" class="task-file">
          <font-awesome-icon icon="link" class="task-file__icon"/>
          <div class="task-file__text">
            <a :href="link.url" target="_blank" rel="noopener" qid="create-p-link-item">{{ link.name ? link.name : link.url }}</a>
            <div class="task-file__sub" v-if="link.name">{{ link.url }}</div>
          </div>
          <span class="v2-field__icon v2-field__icon--danger" @click="openDeleteLinkModal(link)" qid="view-p-remove-link">
            <font-awesome-icon icon="trash-alt"/>
          </span>
        </div>
        <div v-if="!(selectedTask.media || []).length && !(selectedTask.links || []).length" class="v2-field__empty task-file__empty">{{ $t('tasks.v2.no_attachments') }}</div>
        <div v-if="showInput" class="task-link-form">
          <b-form-input type="text" v-model="inputLink" :placeholder="$t('maintenance.third_parties.create.links_placeholder')" qid="create-p-links"></b-form-input>
          <b-form-input type="text" v-model="inputLinkName" :placeholder="inputLink ? inputLink : $t('table.name')" qid="create-p-links"></b-form-input>
          <b-button size="sm" variant="success" @click="handleLink" qid="handle-link-save">{{ $t('system.save') }}</b-button>
        </div>
        <div class="task-file__hint">{{ $t('attachments.file_size_label') }}</div>
      </section>

      <template #rail>
        <RelatedAssetTasks v-if="selectedTask && selectedTask.id" :task="selectedTask" @open="switchTask"></RelatedAssetTasks>
        <TaskPanelHelp v-if="selectedTask && selectedTask.project"
                       :project="selectedTask.project"
                       :can-open-project="isOwner(selectedTask.project)"
                       :guide="getGuide('control_task') ? wpGuide : null"
                       :welcome-guide="welcomeGuide"
                       @open-guide="openGuide()"
                       @open-welcome="openWelcomePage()"
                       @download="downloadMedia"
        ></TaskPanelHelp>
      </template>

      <template #hint>
        <span v-if="selectedTask && selectedTask.project">{{ $t('projects.hero.task_progress') }} <b>{{ project_progress }}%</b></span>
      </template>
      <template #actions>
        <b-button variant="success"
                  v-if="selectedTask.status != 'completed'"
                  @click="completeTask()"
                  :disabled="buttonDisabled"
                  qid="create-p-complete-button"
        ><font-awesome-icon icon="check" class="mr-1"/>{{ $t('tasks.v2.finalize') }}</b-button>
        <b-button variant="success"
                  v-if="selectedTask.status == 'completed'"
                  @click="saveTask()"
                  :disabled="buttonDisabled"
                  qid="create-p-save-button"
        >{{ $t('system.save') }}</b-button>
      </template>
    </TaskPanelFrame>


    <ConfirmRemoveAttachment
        :object="selectedFile"
        :show="showDeleteAttachmentModal"
        type="file"
        @closeConfirmModal="closeDeleteAttachmentModal"
    ></ConfirmRemoveAttachment>

    <ConfirmRemoveAttachment
        :object="selectedLink"
        :show="showDeleteLinkModal"
        type="link"
        @closeConfirmModal="closeDeleteLinkModal"
    ></ConfirmRemoveAttachment>

    <GuideViewModal
        :guide="selectedGuide"
        :showModal="viewGuideModal"
        @closeViewModal="closeViewGuideModal"
    ></GuideViewModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </span>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import RelatedAssetTasks from '@/components/tasks/RelatedAssetTasks.vue';
  import TaskPanelFrame from '@/components/tasks/TaskPanelFrame.vue';
  import TaskPanelHelp from '@/components/tasks/TaskPanelHelp.vue';
  import FormattedNoteEditor from '@/components/tasks/FormattedNoteEditor.vue';
  import { controlResult, controlResultOptions } from '@/util/controlResult.js';
  import { assetIcon } from '@/components/tasks/taskPanel.js';
  import Datepicker from 'vuejs-datepicker';
  import {config} from '@/config.js';
  import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
  import GuideViewModal from '@/components/guide/GuideViewModal.vue';
  import roleCheck from "@/util/ACL/roleCheck"
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';


  export default {
    name: 'RiskSingleTaskModal',
    data: function() {
      return {
        taskForm: {
          remarks: ''
        },
        selectedTask: [],
        taskList: [],
        selectedAnswer: [],
        allAnswers: controlResultOptions(this.$t.bind(this)),
        buttonDisabled: false,
        selectedFile: {},
        selectedLink: {},
        showDeleteAttachmentModal: false,
        showDeleteLinkModal: false,
        showInput: false,
        showSingleLinkLabel: true,
        inputLink: '',
        inputLinkName: '',
        descriptionLength: config.descriptionLength,
        showFullProjectMessage: false,
        showFullGenericControlDescription: false,
        showFullGenericControlPassedDescription: false,
        showFullGenericControlFailedDescription: false,
        error: '',
        project_progress: 0,
        taskCompleted: false,
        welcomeGuide: {},
        selectedGuide: {},
        showFullGuideDescription: false,
        wpGuide: {},
        myGuides: [],
        viewGuideModal: false,
        selectedItem: {},
        showUsersModal: false
      }
    },
    props: {
      task: {}
    },
    components: {
      TaskPanelFrame,
      TaskPanelHelp,
      FormattedNoteEditor,
      RelatedAssetTasks,
      Datepicker,
      ConfirmRemoveAttachment,
      GuideViewModal,
      Avatar,
      ShowMoreModal
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getWordpressLink() {
        return this.$store.state.wordpressLink
      },
      getUser() {
        return this.$store.state.user
      },
    },
    watch: {
      getClient: function () {

      }
    },
    methods: {
      assetIcon,
      getReducedUsers(users) {
        let reducedUsers = []
        if (users) {
          if (users.length > 3) {
            reducedUsers = _.cloneDeep(users);
            reducedUsers = reducedUsers.slice(0, 3);
          } else {
            reducedUsers = users;
          }
        }
        return reducedUsers;
      },
      openGuide() {
        if (this.wpGuide && this.wpGuide.link) {
          window.open(this.wpGuide.link, '_blank')
        }
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Task' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Task',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
            piincHttp.get(self.getWordpressLink + self.myGuides[0].external_id).then(function(response) {
              self.wpGuide = response.data
            }, function() {});
          }
        }, function() {});
      },
      getDifference(endDate) {
        var moment = require('moment');
        let currentDate = moment()
        endDate = moment(endDate);
        let difference = endDate.diff(currentDate, 'days');
        return difference;
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      showTaskModal(object) {

        let self = this;
        this.error = '';
        this.loadGuides('control_task')

        if (object != null) {
          self.selectedTask = object
        }

        if (object == null && this.tasks && this.tasks.length) {
          self.selectedTask = this.tasks[0]
        }

        this.loadTaskById(this.selectedTask.id, true);
        this.populateForm()
        this.$refs['task-modal'].show()
        this.loadTaskGuide()
      },
      // Opens another task on the same asset in the panel, saving this one as Next does (US 9478)
      switchTask(task) {
        let self = this;
        this.error = '';
        if (this.selectedTask.status !== 'completed') {
          piincHttp.put('tasks/' + this.selectedTask.id, {
            remarks: this.taskForm.remarks,
            completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : undefined
          }).then(function() {}, function(error) {
            self.error = error.data && error.data.message ? error.data.message : '';
          });
        }
        this.selectedTask = task;
        this.loadTaskById(task.id);
      },
      hideTaskModal() {
        // Kept so the task can be opened again from the sidebar
        this.$store.commit('storeLastClosedTask', this.selectedTask);
        let self = this;
        this.error = '';

        setTimeout(function(){
          self.$emit('reloadContent');
          self.selectedTask = {}
        }, 500);

        if (!this.taskCompleted && this.selectedTask.status != 'completed') {
          this.saveTask();
          this.minimizeTask();
        }

        if (this.taskCompleted) {
          self.$store.commit('storeReloadTasks');
        }

        this.$refs['task-modal'].hide()
      },
      populateForm() {
        let self = this;

        self.taskForm.remarks = self.selectedTask.remarks

        self.selectedAnswer = []
        if (self.selectedTask.completion_status) {
          // Pass and fail from before US 9478 are shown as Completed and Deviations
          const result = controlResult(self.selectedTask.completion_status);
          self.selectedAnswer = result ? _.find(self.allAnswers, { value: result.value }) : null;
        }
      },
      openWelcomePage() {
        this.showViewGuideModal(this.welcomeGuide)
      },
      showViewGuideModal(object) {
        this.selectedGuide = object
        this.viewGuideModal = true
      },
      closeViewGuideModal() {
        this.selectedGuide = ''
        this.viewGuideModal = false
      },
      loadTaskGuide() {
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';
        let self = this;
        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Task',
            scope: 'control_task',
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.welcomeGuide = response.data.items[0]
          }
        }, function() {});
      },
      navigateToTask(object) {
        if (this.isOwner(object.model)) {
          this.$store.commit('storeTaskToolbarState', true);
          this.$store.commit('storeActiveTask', object);
          if (object.model_type == 'App\\Activity\\Models\\Activity') {
            this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }, query: { task: object.id }})
          }
          if (object.model_type == 'App\\Process\\Models\\Process') {
            this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }, query: { task: object.id }})
          }
          if (object.model_type == 'App\\System\\Models\\System') {
            this.$router.push({ name: 'systems.view', params: { slug: object.model.slug, }, query: { task: object.id }})
          }
          if (object.model_type == 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty') {
            this.$router.push({ name: 'contracts.view', params: { slug: object.model.slug, }, query: { task: object.id }})
          }
          if (object.model_type == 'App\\Maintenance\\Policy\\Models\\Policy') {
            this.$router.push({ name: 'policies.view', params: { slug: object.model.slug, }, query: { task: object.id }})
          }
        }
      },
      navigateToControl(object) {
        if (this.isOwner(object.scope)) {
          this.$store.commit('storeTaskToolbarState', true);
          this.$store.commit('storeActiveTask', object);
          this.$router.push({ name: 'generic_controls.view', params: { slug: object.scope.slug }})
        }
      },
      navigateToProject(task) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', task);
        this.$router.push({ name: 'projects.view', params: { slug: task.project.slug }})
      },
      isOwner(object) {
        let self = this;
        let allow = false;
        let existingUser = _.find(object.users, function(user) {
          return user.id == self.getUser.id
        });

        if (existingUser) {
          allow = true
        } else {
          if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
            allow = true
          }
        }

        return allow
      },
      minimizeTask() {
        var element = document.getElementsByClassName('modal');
        // Task panels aren't modals (US 9478)
        if (element.length) {
          element[0].classList.add("bottom-left");
        }
        // this.submitForm = true
        this.$store.commit('storeLastClosedTask', this.selectedTask);
      },
      saveTask() {
        this.buttonDisabled = true;
        let self = this;

        piincHttp.put('tasks/' + this.selectedTask.id, {
          remarks: this.taskForm.remarks,
          completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : ''
        }).then(function(response) {
          self.buttonDisabled = false;
          self.$emit('reloadContent', response.data);
          self.$refs['task-modal'].hide()
          self.$toastr('success', self.$t('tasks.modal.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })

      },
      completeTask() {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        this.error = ''

        if(!this.selectedTask.model || !this.selectedTask.scope){

          piincHttp.put('tasks/' + this.selectedTask.id, {
            remarks: this.taskForm.remarks,
            completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : undefined,
            status: 'completed'
          }).then(function(response) {
            self.$toastr('success', self.$t('tasks.modal.success'))
            self.buttonDisabled = false;

            if (self.taskList.length > 1) {
              let index = _.findIndex(self.taskList, function(o) { return o.id == self.selectedTask.id })
              if (index < self.taskList.length - 1) {
                self.selectedTask = self.taskList[index + 1]
              } else {
                self.selectedTask = self.taskList[0]
              }
              self.taskList.splice(index, 1);
              self.loadTaskById(self.selectedTask.id);
            } else {
              self.taskCompleted = true;
              self.$store.commit('storeTaskToolbarState', false);
              self.hideTaskModal()
            }
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        }else{
          if (
                  ((this.selectedTask && this.selectedTask.add_attachments == 'Mandatory' && ((this.selectedTask.media && this.selectedTask.media.length) || (this.selectedTask.links && this.selectedTask.links.length))) ||
                          (this.selectedTask && this.selectedTask.add_attachments != 'Mandatory')) &&
                  ((this.selectedTask && this.selectedTask.add_description == 'Mandatory' && this.taskForm.remarks) ||
                          (this.selectedTask && this.selectedTask.add_description != 'Mandatory')) &&
                  (this.selectedAnswer && this.selectedAnswer.value)
          ) {
            piincHttp.put('tasks/' + this.selectedTask.id, {
              remarks: this.taskForm.remarks,
              completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : undefined,
              status: 'completed'
            }).then(function(response) {
              self.$toastr('success', self.$t('tasks.modal.success'))
              self.buttonDisabled = false;

              if (self.taskList.length > 1) {
                let index = _.findIndex(self.taskList, function(o) { return o.id == self.selectedTask.id })
                if (index < self.taskList.length - 1) {
                  self.selectedTask = self.taskList[index + 1]
                } else {
                  self.selectedTask = self.taskList[0]
                }
                self.taskList.splice(index, 1);
                self.loadTaskById(self.selectedTask.id);
              } else {
                self.taskCompleted = true;
                self.$store.commit('storeTaskToolbarState', false);
                self.hideTaskModal()
              }
            }, function(error) {
              if (error.status !== 200) {
                self.buttonDisabled = false;
                self.error = error.data.message;
              }
            })
          } else {
            this.error = '';
            self.buttonDisabled = false;
            if (!this.selectedAnswer || (this.selectedAnswer && !this.selectedAnswer.value)) {
              this.error += ' ' + this.$t('tasks.modal.response_error_message')
            }
            if ((this.selectedTask.add_attachments == 'Mandatory' && this.selectedTask.media && !this.selectedTask.media.length) && (this.selectedTask.add_attachments == 'Mandatory' && this.selectedTask.links && !this.selectedTask.links.length)) {
              this.error += ' ' + this.$t('tasks.modal.attachments_error_message')
            }
            if (this.selectedTask.add_description == 'Mandatory' && !this.taskForm.remarks) {
              this.error += ' ' + this.$t('tasks.modal.description_error_message')
            }
          }
        }


      },
      previousTask() {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        this.error = ''

        piincHttp.put('tasks/' + this.selectedTask.id, {
          remarks: this.taskForm.remarks,
          completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : undefined
        }).then(function(response) {
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })

        let index = _.findIndex(self.taskList, function(o) { return o.id == self.selectedTask.id })
        if (index > 0) {
          this.selectedTask = this.taskList[index - 1]
        } else {
          this.selectedTask = this.taskList[this.taskList.length - 1]
        }

        self.loadTaskById(this.selectedTask.id);

      },
      nextTask() {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        this.error = ''

        piincHttp.put('tasks/' + this.selectedTask.id, {
          remarks: this.taskForm.remarks,
          completion_status: this.selectedAnswer && this.selectedAnswer.value ? this.selectedAnswer.value : undefined
        }).then(function(response) {
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })

        let index = _.findIndex(self.taskList, function(o) { return o.id == self.selectedTask.id })
        if (index < this.taskList.length - 1) {
          this.selectedTask = this.taskList[index + 1]
        } else {
          this.selectedTask = this.taskList[0]
        }

        self.loadTaskById(self.selectedTask.id);

      },
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      loadTaskById(id, ignorePopulate) {
        let self = this;
        piincHttp.get('tasks/' + id).then(function(response) {
          self.selectedTask = response.data;
          self.project_progress = self.selectedTask.project.user_progress.progress;
          if (!ignorePopulate) {
            self.populateForm()
          }
        }, function() {});
      },
      openDeleteAttachmentModal(object) {
        this.selectedFile = object
        this.showDeleteAttachmentModal = true
      },
      closeDeleteAttachmentModal () {
        this.selectedFile = {}
        this.showDeleteAttachmentModal = false;
        this.loadTaskById(this.selectedTask.id, true);
      },
      openDeleteLinkModal(object) {
        this.selectedLink = object
        this.showDeleteLinkModal = true
      },
      closeDeleteLinkModal () {
        this.selectedLink = {}
        this.showDeleteLinkModal = false;
        this.loadTaskById(this.selectedTask.id, true);
      },
      handleLink() {
        let self = this;
        let formData = new FormData();

        if (!this.inputLink.startsWith('http')) {
          this.inputLink = 'http://' + this.inputLink
        }

        formData.append("url", this.inputLink);
        formData.append("name", this.inputLinkName);
        formData.append("model_type", this.selectedTask.who_am_i);
        formData.append("model_id", this.selectedTask.id);

        piincHttp.post('links', formData).then(function() {
          self.inputLink = ''
          self.inputLinkName = ''
          self.loadTaskById(self.selectedTask.id, true);
          self.showInput = false
        }, function() {})
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {
          responseType: 'blob',
          params:
            {
              client: this.getClient.slug,
              task: this.selectedTask.id
            }
        }).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      downloadScopeMedia(object){
        piincHttp.get('tasks/'+this.selectedTask.id+'/documents/' + object.id + '/download', {
          responseType: 'blob',
          params:
                  {
                    client: this.getClient.slug
                  }
        }).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      onFileChange(file) {
        let files = file.target.files;
        let self = this;
        let formData = new FormData();
        let sizeError = false;

        for (var i = 0; i < files.length; i++) {
          // Equal to 10 MB
          if (files[i].size > 10485760) {
            sizeError = true;
          }
          formData.append('document[]', files[i]);
        }

        formData.append("name", "documents");
        formData.append("model_type", this.selectedTask.who_am_i);
        formData.append("model_id", this.selectedTask.id);

        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          piincHttp.post('documents', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function() {
            self.error = ''
            self.loadTaskById(self.selectedTask.id, true);
          }, function(error) {
            self.error = error.data.message;
            if (error.data.errors['document.0']) {
              self.error += ' ' + error.data.errors['document.0'][0]
            }
          })
        }
      },
    },
    created () {

    }
  }
</script>
